// Host-admin Companies page. Lists every tenant, lets the host onboard a
// new one, suspend/resume/archive existing ones, reset a locked-out tenant
// admin, and download a per-tenant DB backup.
//
// Authenticates via the separate host JWT in localStorage('identium_host_token').
// Note we deliberately don't use the regular `api` axios instance for these
// calls — that one attaches the tenant token. We use a small fresh axios
// for host calls.

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  MdAdminPanelSettings, MdAdd, MdBlock, MdPlayArrow, MdArchive, MdDelete,
  MdLockReset, MdCloudDownload, MdLogout, MdContentCopy, MdRefresh,
} from 'react-icons/md';
import { confirm, prompt } from '../../lib/dialogs';
import SearchableSelect from '../../components/SearchableSelect';

const hostApi = axios.create({ baseURL: (process.env.PUBLIC_URL || '') + '/api/system' });
hostApi.interceptors.request.use((config) => {
  const t = localStorage.getItem('identium_host_token');
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});
hostApi.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('identium_host_token');
      localStorage.removeItem('identium_host_user');
      window.location.href = `${process.env.PUBLIC_URL || ''}/host/login`;
    }
    return Promise.reject(err);
  }
);

export default function HostCompanies() {
  const nav = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [createResult, setCreateResult] = useState(null);
  const [resetResult, setResetResult] = useState(null);
  const [error, setError] = useState('');

  const reload = async () => {
    setLoading(true);
    try {
      const r = await hostApi.get('/tenants');
      setTenants(r.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load tenants');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); }, []);

  const logout = async () => {
    try { await hostApi.post('/auth/logout'); } catch {}
    localStorage.removeItem('identium_host_token');
    localStorage.removeItem('identium_host_user');
    nav('/host/login');
  };

  const suspend = async (t) => {
    if (!(await confirm(`Suspend "${t.name}"? Users will be logged out and login will be blocked until you resume.`))) return;
    await hostApi.post(`/tenants/${t.id}/suspend`); reload();
  };
  const resume = async (t) => { await hostApi.post(`/tenants/${t.id}/resume`); reload(); };
  const archive = async (t) => {
    if (!(await confirm(`Archive "${t.name}"? The DB file is preserved but the company is locked out. Reversible.`))) return;
    await hostApi.post(`/tenants/${t.id}/archive`); reload();
  };
  const hardDelete = async (t) => {
    if (!(await confirm(`PERMANENTLY DELETE "${t.name}" and its DB file? This cannot be undone. Type the slug to confirm.`))) return;
    const typed = await prompt(`Type "${t.slug}" to confirm permanent deletion:`);
    if (typed !== t.slug) return alert('Slug mismatch — aborted.');
    try {
      await hostApi.delete(`/tenants/${t.id}?confirm=${t.slug}`);
      reload();
    } catch (e) { alert(e.response?.data?.error || 'Delete failed'); }
  };
  const resetAdmin = async (t) => {
    const username = await prompt(`Reset which admin in "${t.name}"? (default: admin)`, 'admin');
    if (username == null) return;
    try {
      const r = await hostApi.post(`/tenants/${t.id}/reset-admin`, { username });
      setResetResult({ tenant: t, ...r.data });
    } catch (e) { alert(e.response?.data?.error || 'Reset failed'); }
  };
  const backup = async (t) => {
    const r = await hostApi.get(`/tenants/${t.id}/backup`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([r.data]));
    const a = document.createElement('a');
    a.href = url; a.download = `${t.slug}-${new Date().toISOString().slice(0,10)}.db`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => window.URL.revokeObjectURL(url), 30_000);
  };

  return (
    <div style={{ padding: 20, fontFamily: 'inherit', minHeight: '100vh', background: '#f4f6fa' }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <MdAdminPanelSettings style={{ fontSize: 28, color: '#6a1b9a' }} />
        <h1 style={{ margin: 0, fontSize: 22 }}>Host Console — Companies</h1>
        <span style={{ flex: 1 }} />
        <button onClick={reload} className="btn btn-outline btn-sm"><MdRefresh /> Refresh</button>
        <button onClick={() => setShowCreate(true)} className="btn btn-primary btn-sm"><MdAdd /> New company</button>
        <button onClick={logout} className="btn btn-outline btn-sm"><MdLogout /> Sign out</button>
      </header>

      {error && <div className="alert alert-danger" style={{ marginBottom: 16 }}>{error}</div>}

      <div className="card">
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Slug</th>
                <th>Name</th>
                <th>Plan</th>
                <th>Limits</th>
                <th>Status</th>
                <th>Counts</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: 24 }}>Loading…</td></tr>
              ) : tenants.length === 0 ? (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: 24, color: '#888' }}>No companies onboarded yet</td></tr>
              ) : tenants.map(t => (
                <tr key={t.id}>
                  <td><code style={{ fontSize: 12, background: '#eef1f6', padding: '2px 6px', borderRadius: 4 }}>{t.slug}</code></td>
                  <td style={{ fontWeight: 600 }}>{t.name}</td>
                  <td><span className={`badge ${t.plan === 'enterprise' ? 'badge-success' : t.plan === 'trial' ? 'badge-warning' : 'badge-info'}`}>{t.plan}</span></td>
                  <td style={{ fontSize: 12, color: '#666' }}>
                    {t.max_users} users · {t.max_assets.toLocaleString()} assets
                    {t.expires_at && <><br/>expires {new Date(t.expires_at).toLocaleDateString()}</>}
                  </td>
                  <td>
                    <span className={`badge ${t.status === 'active' ? 'badge-success' : t.status === 'suspended' ? 'badge-warning' : 'badge-secondary'}`}>
                      {t.status}
                    </span>
                  </td>
                  <td style={{ fontSize: 12, color: '#666' }}>
                    {t.counts?.error
                      ? <span style={{ color: '#c62828' }}>{t.counts.error}</span>
                      : <>{t.counts?.users ?? 0} users · {t.counts?.assets ?? 0} assets</>
                    }
                  </td>
                  <td style={{ fontSize: 12, color: '#666' }}>{(t.created_at || '').slice(0, 10)}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      {t.status === 'active' && (
                        <button className="btn btn-icon btn-sm btn-outline" title="Suspend" onClick={() => suspend(t)}><MdBlock /></button>
                      )}
                      {t.status === 'suspended' && (
                        <button className="btn btn-icon btn-sm btn-success" title="Resume" onClick={() => resume(t)}><MdPlayArrow /></button>
                      )}
                      <button className="btn btn-icon btn-sm btn-outline" title="Reset tenant admin password" onClick={() => resetAdmin(t)}><MdLockReset /></button>
                      <button className="btn btn-icon btn-sm btn-outline" title="Download DB backup" onClick={() => backup(t)}><MdCloudDownload /></button>
                      {t.status !== 'archived' && (
                        <button className="btn btn-icon btn-sm btn-outline" title="Archive" onClick={() => archive(t)}><MdArchive /></button>
                      )}
                      <button className="btn btn-icon btn-sm btn-danger" title="DELETE permanently" onClick={() => hardDelete(t)}><MdDelete /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showCreate && (
        <CreateModal
          onClose={() => setShowCreate(false)}
          onCreated={(payload) => { setShowCreate(false); setCreateResult(payload); reload(); }}
        />
      )}

      {createResult && (
        <CredentialsModal
          title={`Company "${createResult.tenant.name}" created`}
          subtitle="Share these credentials securely with the company admin. They will be forced to change the password on first login."
          rows={[
            ['Company slug',  createResult.tenant.slug],
            ['Login URL',     window.location.origin + (process.env.PUBLIC_URL || '') + '/login'],
            ['Username',      createResult.initialAdmin?.username || '—'],
            ['Temp password', createResult.initialAdmin?.temporary_password || '—'],
          ]}
          onClose={() => setCreateResult(null)}
        />
      )}
      {resetResult && (
        <CredentialsModal
          title={`Reset admin for "${resetResult.tenant.name}"`}
          subtitle={resetResult.hint}
          rows={[
            ['Company slug',  resetResult.tenant.slug],
            ['Username',      resetResult.username],
            ['Temp password', resetResult.temporary_password],
          ]}
          onClose={() => setResetResult(null)}
        />
      )}
    </div>
  );
}

function CreateModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    slug: '', name: '',
    contact_name: '', contact_email: '', contact_phone: '',
    plan: 'standard', max_users: 25, max_assets: 10000, expires_at: '',
    admin_username: 'admin', admin_email: '', admin_full_name: 'System Administrator',
  });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');

  const submit = async () => {
    setErr('');
    if (!form.slug || !form.name) return setErr('Slug and name are required');
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        max_users: Number(form.max_users) || 25,
        max_assets: Number(form.max_assets) || 10000,
        expires_at: form.expires_at || null,
      };
      const r = await hostApi.post('/tenants', payload);
      onCreated(r.data);
    } catch (e) {
      setErr(e.response?.data?.error || 'Create failed');
    } finally { setSubmitting(false); }
  };

  return (
    <Modal title="Onboard a new company" onClose={onClose}>
      {err && <div className="alert alert-danger" style={{ marginBottom: 12 }}>{err}</div>}
      <Row label="Slug (lowercase, hyphens only)">
        <input value={form.slug} onChange={e => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
               placeholder="bata" className="form-control" />
      </Row>
      <Row label="Display name">
        <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
               placeholder="Bata India Ltd." className="form-control" />
      </Row>
      <Row label="Plan">
        <SearchableSelect value={form.plan} onChange={e => setForm({ ...form, plan: e.target.value })} className="form-control">
          <option value="trial">Trial</option>
          <option value="standard">Standard</option>
          <option value="enterprise">Enterprise</option>
        </SearchableSelect>
      </Row>
      <Row label="Max users">
        <input type="number" min={1} value={form.max_users} onChange={e => setForm({ ...form, max_users: e.target.value })} className="form-control" />
      </Row>
      <Row label="Max assets">
        <input type="number" min={1} value={form.max_assets} onChange={e => setForm({ ...form, max_assets: e.target.value })} className="form-control" />
      </Row>
      <Row label="Expires (optional)">
        <input type="date" value={form.expires_at} onChange={e => setForm({ ...form, expires_at: e.target.value })} className="form-control" />
      </Row>
      <hr style={{ margin: '14px 0', border: 'none', borderTop: '1px dashed #ccc' }} />
      <h4 style={{ margin: '0 0 8px', fontSize: 13, color: '#555' }}>Initial admin user</h4>
      <Row label="Admin username">
        <input value={form.admin_username} onChange={e => setForm({ ...form, admin_username: e.target.value })} className="form-control" />
      </Row>
      <Row label="Admin email (optional)">
        <input value={form.admin_email} onChange={e => setForm({ ...form, admin_email: e.target.value })} className="form-control" type="email" />
      </Row>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
        <button onClick={onClose} className="btn btn-outline">Cancel</button>
        <button onClick={submit} disabled={submitting} className="btn btn-primary">
          {submitting ? 'Creating...' : 'Create company'}
        </button>
      </div>
    </Modal>
  );
}

function CredentialsModal({ title, subtitle, rows, onClose }) {
  const copy = (val) => navigator.clipboard?.writeText(val);
  return (
    <Modal title={title} onClose={onClose} width={520}>
      <p style={{ fontSize: 13, color: '#555', marginTop: 0 }}>{subtitle}</p>
      <div style={{ background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 8, padding: 12 }}>
        {rows.map(([label, val]) => (
          <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0' }}>
            <div style={{ width: 130, fontSize: 12, color: '#7a5400', fontWeight: 600 }}>{label}</div>
            <code style={{ flex: 1, fontFamily: 'monospace', fontSize: 13, background: 'white', padding: '4px 10px', borderRadius: 6 }}>{val}</code>
            <button onClick={() => copy(val)} className="btn btn-icon btn-sm btn-outline" title="Copy"><MdContentCopy /></button>
          </div>
        ))}
      </div>
      <div style={{ textAlign: 'right', marginTop: 16 }}>
        <button onClick={onClose} className="btn btn-primary">Done</button>
      </div>
    </Modal>
  );
}

function Modal({ title, children, onClose, width = 480 }) {
  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 20,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'white', borderRadius: 14, padding: 20, width: '100%', maxWidth: width,
        maxHeight: '90vh', overflowY: 'auto',
      }}>
        <h3 style={{ margin: '0 0 16px', fontSize: 18 }}>{title}</h3>
        {children}
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#546e7a', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>
        {label}
      </label>
      {children}
    </div>
  );
}
